/**
 * Interactions log — Slice 4 (Feb 2026).
 *
 * Records team interactions with channel partners, distributors, allied
 * industry contacts, etc. Shared org-wide (matches Competition Mapping's
 * visibility model) — feeds three goal metrics:
 *   auto:interaction_count, auto:channel_partner_interactions,
 *   auto:allied_industry_interactions
 *
 * Same shape as CompetitionMapping.js — flat table + modal. Attribution is
 * on `logged_by` and metric anchor is `interaction_date` (not logged_at) so
 * back-dated entries count in the month they actually happened.
 */

import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Plus, MagnifyingGlass, Trash, PencilSimple, Handshake,
} from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import useAuthStore from '../store/authStore';

import { confirm } from '../components/ConfirmDialog';
import { notify } from '../lib/notify';
import { InlineSkeleton } from '../components/Skeletons';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_STYLES = {
  'Channel Partner':  'bg-indigo-100 text-indigo-700',
  'Distributor':      'bg-sky-100 text-sky-700',
  'Allied Industry':  'bg-emerald-100 text-emerald-700',
  'Other':            'bg-slate-100 text-slate-600',
};

/**
 * Format an ISO date string (YYYY-MM-DD) as DD/MM/YYYY without going through
 * JS `Date` — otherwise `new Date('2026-06-15').toLocaleDateString('en-IN')`
 * parses as UTC midnight and can shift a day back in IST (flagged in iter55
 * code review). Handles full ISO timestamps too (splits on 'T').
 */
const fmtDate = (iso) => {
  if (!iso) return '';
  const datePart = String(iso).split('T')[0];
  const parts = datePart.split('-');
  if (parts.length !== 3) return iso;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

export default function Interactions() {
  const { user } = useAuthStore();
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const canManage = ['National Sales Manager', 'Vice President', 'President'].includes(user?.role);

  const load = async () => {
    setLoading(true); setError('');
    try {
      const params = typeFilter ? `?interaction_type=${encodeURIComponent(typeFilter)}` : '';
      const r = await axios.get(`${API}/interactions${params}`);
      setItems(r.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load interactions');
      setItems([]);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    axios.get(`${API}/interactions/types`).then(r => setTypes(r.data.types || []));
  }, []);

  useEffect(() => { load(); }, [typeFilter]); // eslint-disable-line

  const filtered = items.filter(it => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (it.entity_name || '').toLowerCase().includes(q) ||
      (it.contact_person || '').toLowerCase().includes(q) ||
      (it.logged_by_name || '').toLowerCase().includes(q) ||
      (it.notes || '').toLowerCase().includes(q)
    );
  });

  const del = async (id) => {
    if (!await confirm({ message: 'Delete this interaction?' })) return;
    try { await axios.delete(`${API}/interactions/${id}`); await load(); }
    catch (e) { notify(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="p-6" data-testid="interactions-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }}>Interactions</h1>
          <p className="text-sm text-[#6D8E87] mt-1">Log meetings with channel partners, distributors, and allied industry contacts. Shared org-wide — feeds interaction-count goal metrics.</p>
        </div>
        <button
          onClick={() => { setEditing(null); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-[#00a69c] text-white rounded-lg hover:bg-[#008f86] transition"
          data-testid="create-interaction-btn"
        >
          <Plus size={18} weight="bold" /> Log Interaction
        </button>
      </div>

      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6D8E87]" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search entity, contact, or logger"
            className="w-full pl-9 pr-4 py-2 border border-[#D5E8E4] rounded-lg text-sm"
            data-testid="int-search" />
        </div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          className="px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm bg-white"
          data-testid="int-type-filter">
          <option value="">All types</option>
          {types.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <span className="text-xs text-[#6D8E87]" data-testid="int-count">{filtered.length} of {items.length}</span>
      </div>

      {loading ? (
        <InlineSkeleton />
      ) : error ? (
        <div className="medical-card p-6 border border-rose-200 bg-rose-50" data-testid="int-load-error">
          <p className="text-sm text-rose-700">{error}</p>
          <button onClick={load} className="mt-2 text-xs text-rose-800 underline">Retry</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="medical-card p-12 text-center" data-testid="int-empty">
          <Handshake size={40} className="mx-auto text-[#B5D9D2] mb-3" />
          <p className="text-[#3B5A54] font-medium">No interactions logged yet.</p>
          <p className="text-sm text-[#6D8E87] mt-1">Record every meaningful contact with partners, distributors, and allied industry.</p>
        </div>
      ) : (
        <div className="medical-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F9F7] text-left text-[11px] uppercase tracking-wide text-[#3B5A54]">
              <tr>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Entity</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Logged By</th>
                <th className="px-4 py-3">Notes</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(it => {
                const isOwner = it.logged_by === user?.sub || it.logged_by === user?.id;
                const canEdit = isOwner || canManage;
                return (
                  <tr key={it.id} className="border-t border-[#F0F5F3] hover:bg-[#FAFCFB]" data-testid={`int-row-${it.id}`}>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${TYPE_STYLES[it.interaction_type] || TYPE_STYLES.Other}`}>
                        {it.interaction_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-[#052A23]">{it.entity_name}</td>
                    <td className="px-4 py-3 text-[#3B5A54]">
                      {it.contact_person || <span className="text-[#98B4AE] italic">—</span>}
                      {(it.phone || it.email) && <div className="text-[11px] text-[#6D8E87]">{[it.phone, it.email].filter(Boolean).join(' · ')}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#6D8E87]">{fmtDate(it.interaction_date)}</td>
                    <td className="px-4 py-3 text-xs text-[#6D8E87]">{it.logged_by_name} <span className="text-[#98B4AE]">({it.logged_by_role})</span></td>
                    <td className="px-4 py-3 text-xs text-[#3B5A54] max-w-[260px] truncate" title={it.notes}>{it.notes || <span className="text-[#98B4AE] italic">—</span>}</td>
                    <td className="px-4 py-3">
                      {canEdit && (
                        <div className="flex items-center gap-2">
                          <button onClick={() => { setEditing(it); setShowModal(true); }} className="text-[#3B5A54] hover:text-[#052A23]" title="Edit" data-testid={`int-edit-${it.id}`}>
                            <PencilSimple size={16} />
                          </button>
                          <button onClick={() => del(it.id)} className="text-rose-500 hover:text-rose-700" title="Delete" data-testid={`int-delete-${it.id}`}>
                            <Trash size={16} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <InteractionModal
          initial={editing}
          types={types}
          onClose={() => { setShowModal(false); setEditing(null); }}
          onSaved={async () => { setShowModal(false); setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}

const InteractionModal = ({ initial, types, onClose, onSaved }) => {
  const [form, setForm] = useState({
    interaction_type: initial?.interaction_type || (types[0] || 'Channel Partner'),
    entity_name: initial?.entity_name || '',
    contact_person: initial?.contact_person || '',
    phone: initial?.phone || '',
    email: initial?.email || '',
    interaction_date: initial?.interaction_date || new Date().toISOString().slice(0, 10),
    notes: initial?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setErr(''); setSaving(true);
    try {
      if (initial?.id) {
        await axios.put(`${API}/interactions/${initial.id}`, form);
      } else {
        await axios.post(`${API}/interactions`, form);
      }
      await onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{initial ? 'Edit' : 'Log'} Interaction</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[#6D8E87] mb-1">Type *</label>
              <select value={form.interaction_type} onChange={e => setForm({ ...form, interaction_type: e.target.value })}
                className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg" data-testid="int-type">
                {types.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-[#6D8E87] mb-1">Interaction Date *</label>
              <input type="date" value={form.interaction_date} onChange={e => setForm({ ...form, interaction_date: e.target.value })}
                className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg" required data-testid="int-date" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-[#6D8E87] mb-1">Entity Name *</label>
            <input value={form.entity_name} onChange={e => setForm({ ...form, entity_name: e.target.value })}
              placeholder="e.g. Meditek Distributors Ltd" className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg" required data-testid="int-entity" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-[#6D8E87] mb-1">Contact Person</label>
              <input value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })}
                className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg" data-testid="int-contact" />
            </div>
            <div>
              <label className="block text-xs text-[#6D8E87] mb-1">Phone</label>
              <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg" data-testid="int-phone" />
            </div>
            <div>
              <label className="block text-xs text-[#6D8E87] mb-1">Email</label>
              <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg" data-testid="int-email" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-[#6D8E87] mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
              rows={3} placeholder="Discussion summary, action items, next steps…"
              className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg" data-testid="int-notes" />
          </div>
          {err && <div className="p-2 bg-rose-50 border border-rose-200 rounded text-xs text-rose-700" data-testid="int-error">{err}</div>}
          <div className="flex gap-2 pt-2">
            <button onClick={onClose} className="flex-1 py-2 border border-[#D5E8E4] rounded-lg text-sm">Cancel</button>
            <button onClick={submit} disabled={saving || !form.entity_name || !form.interaction_date}
              className="flex-1 py-2 bg-[#00a69c] text-white rounded-lg text-sm disabled:opacity-50" data-testid="int-save-btn">
              {saving ? 'Saving…' : (initial ? 'Save Changes' : 'Log Interaction')}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
